import { observer } from 'mobx-react-lite';
import { useState, useMemo } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  HStack,
  useDisclosure,
  useToast,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import { useStore } from '../../../hooks/useStore';
import { Colors } from '../../../theme/colors';

type Mode = 'verify' | 'remove' | null;

const ManuallyVerifyPhoneNumber = () => {
  const {
    userStore: { displayedUser, updateUser },
  } = useStore();

  const bgColor = useColorModeValue(Colors.light.background, Colors.dark.background);
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const modal = useDisclosure();
  const [mode, setMode] = useState<Mode>(null);

  // form state used only in "verify" mode
  const [countryCode, setCountryCode] = useState(displayedUser.countryCode ?? '+1');
  const [phoneNumber, setPhoneNumber] = useState(displayedUser.phoneNumber ?? '');

  const isVerified = displayedUser.isPhoneVerified;
  const buttonScheme = useMemo(() => (isVerified ? 'red' : 'green'), [isVerified]);

  const open = () => {
    setMode(isVerified ? 'remove' : 'verify');
    modal.onOpen();
  };

  const onClose = () => {
    modal.onClose();
    setMode(null);
    // reset inputs back to current user values so reopening starts clean
    setCountryCode(displayedUser.countryCode ?? '+1');
    setPhoneNumber(displayedUser.phoneNumber ?? '');
  };

  const handleVerify = async () => {
    try {
      setLoading(true);
      const trimmed = (phoneNumber || '').trim();
      if (!trimmed) {
        toast({ title: 'Phone number required', status: 'warning', duration: 2500, isClosable: true });
        return;
      }

      await updateUser(displayedUser.id, {
        countryCode: (countryCode || '+1').trim(),
        phoneNumber: trimmed,
        isPhoneVerified: true,
      });

      toast({
        title: 'Phone verified',
        description: 'The phone number was set and marked as verified.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      onClose();
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to verify phone', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setLoading(true);

      await updateUser(displayedUser.id, {
        isPhoneVerified: false,
        phoneNumber: null,
      });

      toast({
        title: 'Verification removed',
        description: 'The user’s phone verification was cleared.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      onClose();
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to remove verification', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        w="100%"
        py="25px"
        onClick={open}
        isLoading={loading}
        colorScheme={buttonScheme}
        variant="solid"
      >
        {isVerified ? 'Remove Phone Verification' : 'Manually Verify Phone Number'}
      </Button>

      <Modal isOpen={modal.isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg={bgColor}>
          <ModalHeader>
            {mode === 'verify' ? 'Manually Verify Phone' : 'Remove Phone Verification'}
          </ModalHeader>

          <ModalBody>
            {mode === 'verify' ? (
              <>
                <HStack spacing={3}>
                  <FormControl maxW="120px">
                    <FormLabel>Country Code</FormLabel>
                    <Input
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      placeholder="+1"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Phone Number</FormLabel>
                    <Input
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="5551234567"
                    />
                  </FormControl>
                </HStack>
                <Text mt={3} fontSize="sm" color="gray.500">
                  This will set the user’s phone number and mark it as verified.
                </Text>
              </>
            ) : (
              <Text>
                Are you sure you want to remove phone verification for{' '}
                <strong>{displayedUser.userName}</strong>?
              </Text>
            )}
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose} variant="ghost">
              Cancel
            </Button>
            {mode === 'verify' ? (
              <Button colorScheme="green" onClick={handleVerify} isLoading={loading}>
                Save & Verify
              </Button>
            ) : (
              <Button colorScheme="red" onClick={handleRemove} isLoading={loading}>
                Remove Verification
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default observer(ManuallyVerifyPhoneNumber);
